import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { ArrowLeft } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1400&q=80";

export default function Signup() {
  const { signup, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(
    location.state?.needsOtp ? "otp" : "form"
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState(location.state?.email || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const t = setTimeout(
      () => setResendCooldown((c) => c - 1),
      1000
    );

    return () => clearTimeout(t);
  }, [resendCooldown]);


  
  const submitForm = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      return toast.error("Please enter your full name.");
    }

    if (phone.length !== 10) {
      return toast.error("Enter a valid 10-digit mobile number.");
    }

    if (password.length < 6) {
      return toast.error(
        "Password must be at least 6 characters."
      );
    }

    setSubmitting(true);

    try {
      const res = await signup(
        name,
        email,
        phone,
        password
      );

      toast.success(res.message);

      if (res.dev_otp) {
        setDevOtp(res.dev_otp);

        toast(`Dev OTP: ${res.dev_otp}`, {
          duration: 12000,
        });
      }

      setStep("otp");
      setResendCooldown(45);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Signup failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();

    if (otp.length !== 6) {
      return toast.error(
        "Enter the 6-digit code"
      );
    }

    setSubmitting(true);

    try {
      await verifyOtp(email, otp);

      toast.success(
        "Welcome to INDUS EIRENE"
      );

      navigate("/profile");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Verification failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      const res = await resendOtp(email);

      toast.success(res.message);

      if (res.dev_otp) {
        setDevOtp(res.dev_otp);

        toast(`Dev OTP: ${res.dev_otp}`, {
          duration: 12000,
        });
      }

      setResendCooldown(45);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not resend"
      );
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-80px)] grid md:grid-cols-2"
      data-testid="signup-page"
    >
      <div className="hidden md:block relative">
        <img
          src={HERO}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />

        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <div className="ie-overline opacity-80 mb-3">
            Begin a new ritual
          </div>

          <div className="font-serif text-4xl leading-tight">
            Crafted in India · Worn Worldwide
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-6 py-12 md:py-16 md:px-20">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {step === "form" ? (
              <motion.div
                key="form"
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -16,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <div className="ie-overline text-brand-accent mb-4">
                  Create Account
                </div>

                <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
                  Begin a quiet ritual.
                </h1>

                <p className="text-sm text-text-secondary leading-relaxed mb-10">
                  Create an account to receive private editions,
                  atelier care, and complimentary alterations.
                </p>

                <form
                  onSubmit={submitForm}
                  className="space-y-8"
                  data-testid="signup-form"
                >
                  {/* Full Name */}
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Full Name
                    </label>

                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) =>
                        setName(e.target.value)
                      }
                      className="ie-input"
                      data-testid="signup-name"
                      autoComplete="name"
                    />
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Mobile Number
                    </label>

                    <div className="flex">
                      <div className="flex items-center px-4 border border-r-0 border-border bg-muted/30 text-sm font-serif">
                        +91
                      </div>

                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);

                          setPhone(value);
                        }}
                        className="ie-input flex-1"
                        data-testid="signup-phone"
                        autoComplete="tel"
                        inputMode="numeric"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        placeholder="Enter 10-digit mobile number"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Email
                    </label>

                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) =>
                        setEmail(e.target.value)
                      }
                      className="ie-input"
                      data-testid="signup-email"
                      autoComplete="email"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Password
                    </label>

                    <input
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) =>
                        setPassword(e.target.value)
                      }
                      className="ie-input"
                      data-testid="signup-password"
                      autoComplete="new-password"
                    />

                    <div className="text-[11px] text-text-secondary mt-2">
                      Minimum 6 characters.
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="ie-btn-primary w-full justify-center"
                    data-testid="signup-submit"
                  >
                    {submitting
                      ? "Creating account…"
                      : "Continue"}
                  </button>
                </form>

                <div className="mt-10 pt-6 border-t border-border text-sm text-text-secondary">
                  Already in the house?{" "}

                  <Link
                    to="/login"
                    className="text-foreground hover:text-brand-accent ie-overline ml-1"
                    data-testid="signup-go-login"
                  >
                    Sign In
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="otp"
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -16,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <button
                  onClick={() => setStep("form")}
                  className="ie-overline text-text-secondary hover:text-foreground inline-flex items-center gap-2 mb-8"
                  data-testid="otp-back"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Edit details
                </button>

                <div className="ie-overline text-brand-accent mb-4">
                  Verify your email
                </div>

                <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
                  A code is on its way.
                </h1>

                <p className="text-sm text-text-secondary leading-relaxed mb-10">
                  We've sent a 6-digit verification code to{" "}
                  <span className="font-serif text-foreground">
                    {email}
                  </span>
                  . Enter it below to complete your account.
                </p>

                {devOtp && (
                  <div
                    className="border border-brand-accent/40 bg-brand-accent/5 px-4 py-3 mb-6 text-xs"
                    data-testid="dev-otp-banner"
                  >
                    <div className="ie-overline text-brand-accent mb-1">
                      Dev fallback (email service unavailable)
                    </div>

                    <div className="font-serif text-2xl tracking-[0.4em] text-foreground">
                      {devOtp}
                    </div>
                  </div>
                )}

                <form
                  onSubmit={submitOtp}
                  className="space-y-8"
                  data-testid="otp-form"
                >
                  <div
                    className="flex justify-center"
                    data-testid="otp-input-wrap"
                  >
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map(
                          (i) => (
                            <InputOTPSlot
                              key={i}
                              index={i}
                              className="w-12 h-14 text-lg font-serif border-border data-[active=true]:ring-brand-accent data-[active=true]:border-brand-accent rounded-none"
                            />
                          )
                        )}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <button
                    type="submit"
                    disabled={
                      submitting ||
                      otp.length !== 6
                    }
                    className="ie-btn-primary w-full justify-center"
                    data-testid="otp-submit"
                  >
                    {submitting
                      ? "Verifying…"
                      : "Verify & Enter"}
                  </button>
                </form>

                <div className="mt-8 text-sm text-text-secondary">
                  Didn't receive it?{" "}

                  <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="ie-overline text-foreground hover:text-brand-accent disabled:opacity-50"
                    data-testid="otp-resend"
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend code"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}